<template>
  <div class="min-h-screen bg-gray-50 pb-60">
    <!-- 顶部导航 -->
    <div v-if="!isModal" class="bg-white sticky top-0 z-10 border-b border-gray-200">
      <div class="flex items-center justify-between px-4 h-12">
        <div class="flex items-center" @click="$router.back()">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </div>
        <div class="text-lg font-medium text-gray-900">付款申请详情</div>
        <div class="w-6"></div>
      </div>
    </div>
    <div v-else class="bg-white sticky top-0 z-10 border-b border-gray-200">
       <div class="flex items-center justify-between px-4 h-12">
        <div class="w-6"></div>
        <div class="text-lg font-medium text-gray-900">付款申请详情</div>
        <div class="flex items-center" @click="$emit('close')">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <div v-else-if="error" class="p-4 text-center text-red-500">
      {{ error }}
    </div>

    <div v-else class="p-4 space-y-4">
      <!-- 状态卡片 -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-gray-500 text-sm">申请单号</span>
          <span class="text-gray-900 text-sm font-medium">{{ instanceInfo.businessNo || '-' }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-gray-500 text-sm">当前状态</span>
          <span :class="getStatusClass(currentStatusKey)" class="text-xs px-2 py-1 rounded-full">
            {{ currentStatusTextZh }}
          </span>
        </div>
      </div>

      <!-- 核心信息 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">基本信息</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">申请人</span>
            <span class="text-gray-900">{{ instanceInfo.submitterName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">申请日期</span>
            <span class="text-gray-900">{{ formatDate(instanceInfo.submittedAt) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">供应商</span>
            <span class="text-gray-900 text-right max-w-[60%] truncate">{{ initialData.supplierName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">订单金额</span>
            <span class="text-gray-900 font-medium">¥{{ formatCurrency(initialData.amount) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">实际付款金额</span>
            <span class="text-red-600 font-medium">¥{{ formatCurrency(initialData.totalAmount ?? initialData.amount) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">付款方式</span>
            <span class="text-gray-900">{{ paymentMethodZh }}</span>
          </div>
        </div>
      </div>

      <!-- 详细信息 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">付款详情</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex flex-col">
            <span class="text-gray-500 mb-1">付款事由</span>
            <span class="text-gray-900 bg-gray-50 p-2 rounded">{{ initialData.paymentReason || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">期望付款日期</span>
            <span class="text-gray-900">{{ initialData.expectedPaymentDate || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">结算账户</span>
            <span class="text-gray-900">{{ initialData.paymentAccountName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">收款方</span>
            <span class="text-gray-900">{{ initialData.payeeName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">收款账号</span>
            <span class="text-gray-900">{{ initialData.payeeAccount || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">开户行</span>
            <span class="text-gray-900">{{ initialData.payeeBank || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 进货单列表 -->
      <div v-if="voucherListView.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <div class="flex justify-between items-center border-b pb-2">
          <h3 class="font-medium text-gray-900">进货单列表 ({{ voucherListView.length }})</h3>
          <button @click="toggleVoucherPanel" class="text-blue-600 text-sm">
            {{ isVoucherCollapsed ? '展开' : '收起' }}
          </button>
        </div>
        
        <!-- 汇总信息 -->
        <div class="flex justify-between text-sm text-gray-700 bg-gray-50 p-2 rounded">
          <span>合计金额</span>
          <span class="font-medium text-red-600">¥{{ formatCurrency(voucherTotalAmount) }}</span>
        </div>

        <!-- 列表内容 -->
        <div v-show="!isVoucherCollapsed" class="space-y-3">
          <div v-if="voucherLoading" class="flex justify-center py-4">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span class="ml-2 text-sm text-gray-500">加载明细中...</span>
          </div>
          
          <div v-else v-for="(v, idx) in voucherListView" :key="v.id || v.voucherId || idx" 
            class="bg-gray-50 p-3 rounded text-sm border border-gray-100">
            <div class="flex justify-between mb-1">
              <span class="text-gray-900 font-medium">{{ v.orderNo }}</span>
              <span class="text-red-600 font-medium">¥{{ formatCurrency(v.paymentAmount || v.totalAmount || v.UnPayAmount) }}</span>
            </div>
            <div class="text-xs text-gray-500 flex justify-between mb-1">
              <span>{{ formatDate(v.orderDate || v.date) }}</span>
              <span v-if="v.invoiceNo">发票: {{ v.invoiceNo }}</span>
            </div>
            
            <!-- 详细字段 -->
            <div v-if="v.items" class="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
               <div>仓库: {{ v.warehouseName || '-' }}</div>
               <div>制单: {{ v.creatorName || '-' }}</div>
               <div>状态: 
                 <span v-if="v.paymentStatus" class="text-green-600 ml-1">{{ v.paymentStatus }}</span>
                 <span v-else>-</span>
               </div>
            </div>

            <!-- 商品明细 -->
            <div v-if="v.items && v.items.length" class="mt-2 bg-white rounded p-2 text-xs border border-gray-100">
               <div class="grid grid-cols-12 gap-1 font-medium text-gray-500 border-b border-gray-100 pb-1 mb-1">
                 <div class="col-span-6">商品</div>
                 <div class="col-span-2 text-right">数</div>
                 <div class="col-span-4 text-right">金额</div>
               </div>
               <div v-for="(item, i) in v.items" :key="i" class="grid grid-cols-12 gap-1 py-0.5 text-gray-700">
                 <div class="col-span-6 truncate">{{ item.productName }}</div>
                 <div class="col-span-2 text-right">{{ item.quantity }}</div>
                 <div class="col-span-4 text-right">¥{{ formatCurrency(item.total) }}</div>
               </div>
            </div>
          </div>
          
          <!-- 抄送人 -->
          <div v-if="ccUsersNormalized.length" class="relative pt-2">
             <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white bg-gray-400"></div>
             <div class="flex flex-col">
               <span class="text-sm font-medium text-gray-900">抄送</span>
               <div class="flex flex-wrap gap-1 mt-1">
                 <span v-for="(cc, idx) in ccUsersNormalized" :key="idx" 
                   class="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                   {{ cc.ccUserName || cc.name || cc }}
                 </span>
               </div>
             </div>
          </div>
        </div>
      </div>

      <!-- 附件 -->
      <div v-if="attachmentsNormalized.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">附件</h3>
        <div class="space-y-2">
          <div v-for="(att, i) in attachmentsNormalized" :key="i" 
            class="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100">
            <div class="flex items-center space-x-3 overflow-hidden">
               <!-- 图片显示缩略图 -->
               <div v-if="isImage(att.url || att.fileName)" class="h-12 w-12 flex-shrink-0 overflow-hidden rounded border border-gray-200" @click="openImage(att.url)">
                 <img :src="sanitizeAttachmentUrl(att.url)" class="h-full w-full object-cover" alt="附件" />
               </div>
               <span class="text-sm text-gray-700 truncate flex-1">{{ att.fileName || '附件' }}</span>
            </div>
            <a v-if="!isImage(att.url || att.fileName)" :href="sanitizeAttachmentUrl(att.url)" target="_blank" class="text-blue-600 text-sm flex-shrink-0 ml-2">查看</a>
            <span v-else class="text-blue-600 text-sm flex-shrink-0 ml-2" @click="openImage(att.url)">查看大图</span>
          </div>
        </div>
      </div>

      <!-- 审批流程 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">审批流程</h3>
        <div class="relative pl-4 border-l-2 border-gray-200 space-y-6 my-2">
          <div v-for="(node, index) in workflowNodesPreview" :key="index" class="relative">
            <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white"
              :class="nodeNumberClass(node)"></div>
            <div class="flex flex-col">
              <div class="flex justify-between items-start">
                <span class="text-sm font-medium text-gray-900">{{ node.nodeName }}</span>
                <span class="text-xs text-gray-500" v-if="node.processedAt">{{ formatDate(node.processedAt) }}</span>
              </div>
              <div class="text-xs text-gray-600 mt-1">
                <span v-if="node.approvers && node.approvers.length">
                  {{ node.approvers.map(a => a.approverName).join(', ') }}
                </span>
                <span v-else>待定</span>
              </div>
              <div class="mt-1">
                 <span :class="actionBadgeClass(node)" class="text-xs px-2 py-0.5 rounded">
                   {{ node.actionText || getNodeStatusText(node.action) }}
                 </span>
              </div>
              <div v-if="node.remarks" class="mt-1 text-xs text-gray-500 bg-gray-50 p-1 rounded">
                {{ node.remarks }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div v-if="canApprove" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-50">
      <div class="flex flex-col space-y-3">
        <textarea v-model="approvalComment" rows="2" placeholder="请输入审批意见（可选）"
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
        <div class="flex space-x-3">
          <button @click="handleReject" :disabled="approving"
            class="flex-1 bg-red-50 text-red-600 py-2.5 rounded-lg font-medium active:bg-red-100 disabled:opacity-50">
            驳回
          </button>
          <button @click="handleApprove" :disabled="approving"
            class="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium active:bg-blue-700 shadow-sm shadow-blue-200 disabled:opacity-50">
            通过
          </button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model:visible="showNextApprovalDialog"
      title="操作成功"
      message="还有待办审批，是否继续审批下一条？"
      confirmText="继续审批"
      @confirm="handleNextApprovalConfirm"
      @cancel="goBack"
    />
    <FileViewer
      :visible="showFileViewer"
      :file="previewFile"
      @close="showFileViewer = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { approvalApi, purchaseApi } from '@/api/api.js'
import { toast } from '@/utils/toast.js'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import FileViewer from '@/components/FileViewer.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  isModal: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'refresh'])

const route = useRoute()
const router = useRouter() // init router
const applicationId = computed(() => props.id || route.params.id)

const loading = ref(false)
const error = ref('')
const initialData = ref({})
const instanceInfo = ref({})
const approvalRecords = ref([])
const workflowNodes = ref([])

// 文件预览
const showFileViewer = ref(false)
const previewFile = ref({})
const approvalComment = ref('')
const approving = ref(false)
const isVoucherCollapsed = ref(true)
const voucherLoading = ref(false)
const showNextApprovalDialog = ref(false)
const pendingApprovals = ref([])

// Helpers
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

const formatCurrency = (amount) => {
  return Number(amount || 0).toFixed(4)
}

const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  }
  return map[status] || 'bg-gray-100 text-gray-800'
}

const paymentMethodZh = computed(() => {
  const raw = initialData.value?.paymentMethod || 'bank_transfer'
  const map = {
    'bank_transfer': '银行转账',
    'cash': '现金',
    'check': '支票',
    'wechat': '微信支付',
    'alipay': '支付宝'
  }
  return map[raw.toLowerCase()] || raw
})

// 提取文件名（支持 URL 或原始名称）
const extractFileNameFromUrl = (u) => {
  const s = String(u || '')
  const base = s.split('?')[0]
  const parts = base.split('/')
  return decodeURIComponent(parts.pop() || '附件')
}

// 统一附件结构为 { url, fileName }
const normalizeAttachment = (att) => {
  if (!att) return { url: '', fileName: '附件' }
  if (typeof att === 'string') {
    const url = sanitizeAttachmentUrl(att)
    return { url, fileName: extractFileNameFromUrl(url) }
  }
  const rawUrl =
    att.url ||
    att.fileUrl ||
    att.FileUrl ||
    att.FullUrl ||
    att.href ||
    att.path ||
    att.imageUrl ||
    att.link ||
    att.src ||
    att.file_path ||
    att.filePath ||
    ''
  const url = sanitizeAttachmentUrl(rawUrl || att.name || '')
  const fileName = att.fileName || att.file_name || att.name || extractFileNameFromUrl(url)
  return { url, fileName }
}

const attachmentsNormalized = computed(() => {
  const fromJson = Array.isArray(initialData.value?.attachments) ? initialData.value.attachments : []
  let fromInstance = []
  const raw = instanceInfo.value?.attachmentUrl
  if (typeof raw === 'string' && raw.trim().length > 0) {
    try { const arr = JSON.parse(raw); if (Array.isArray(arr)) fromInstance = arr }
    catch (e) { console.error(e); fromInstance = [] }
  }
  
  const all = [...fromJson, ...fromInstance]
  const seen = new Map()
  const result = []
  
  for (const item of all) {
    const norm = normalizeAttachment(item)
    if (!norm.url || norm.url === '#') continue
    
    const key = norm.url.trim().toLowerCase()
    if (!seen.has(key)) {
      seen.set(key, true)
      result.push(norm)
    }
  }
  return result
})

const sanitizeAttachmentUrl = (url) => {
  if (!url) return '#'
  const s = String(url).replace(/[`\s]+/g, '')
  const m = s.match(/https?:\/\/[^\s"']+/)
  return m ? m[0] : s
}

const isImage = (url) => {
  if (!url) return false
  return /\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i.test(String(url).split('?')[0])
}

const openImage = (att) => {
  if (!att) return
  
  // 如果传入的是字符串URL (兼容旧代码)
  if (typeof att === 'string') {
    const url = att
    const fileName = url.split('/').pop() || 'image.png'
    previewFile.value = {
      url: sanitizeAttachmentUrl(url),
      fileName: fileName,
      name: fileName
    }
  } else {
    // 传入的是附件对象
    previewFile.value = {
      ...att,
      url: sanitizeAttachmentUrl(att.url),
      fileName: att.fileName || att.name || 'image.png',
      name: att.fileName || att.name || 'image.png'
    }
  }
  showFileViewer.value = true
}

const voucherListView = computed(() => {
  const list = initialData.value?.voucherList
  return Array.isArray(list) ? list : []
})

const voucherTotalAmount = computed(() => {
  return voucherListView.value.reduce((sum, v) => sum + (parseFloat(v.paymentAmount) || parseFloat(v.UnPayAmount) || parseFloat(v.totalAmount) || 0), 0)
})

const toggleVoucherPanel = async () => {
  if (isVoucherCollapsed.value) {
    isVoucherCollapsed.value = false
    await fetchVoucherDetails()
  } else {
    isVoucherCollapsed.value = true
  }
}

const fetchVoucherDetails = async () => {
  const list = initialData.value?.voucherList || []
  if (list.length > 0) {
    const needsLoad = list.some(v => !v.items)
    if (needsLoad) {
      voucherLoading.value = true
      try {
        const ids = list.map(v => v.id || v.voucherId).filter(id => id)
        if (ids.length > 0) {
          const res = await purchaseApi.getListByIds(ids)
          
          if (res && res.success && Array.isArray(res.data)) {
            const map = new Map(res.data.map(d => [d.orderId, d]))
            initialData.value.voucherList = list.map(v => {
              const detail = map.get(v.id || v.voucherId)
              if (detail) {
                return { ...detail, ...v }
              }
              return v
            })
          }
        }
      } catch (e) {
        console.error('Fetch voucher details failed', e)
        toast.error('加载进货单详情失败')
      } finally {
        voucherLoading.value = false
      }
    }
  }
}

// Status Logic
const normalizeInstanceStatus = (val) => {
  if (val == null) return 'pending'
  if (typeof val === 'string') {
    const s = val.toLowerCase()
    if (['pending', 'start', '待审批'].includes(s)) return 'pending'
    if (['in_progress', 'inprogress', '审批中'].includes(s)) return 'in_progress'
    if (['approved', '审批通过', '已通过'].includes(s)) return 'approved'
    if (['rejected', '审批驳回', '已驳回'].includes(s)) return 'rejected'
    if (['cancelled', '已取消', '撤销'].includes(s)) return 'cancelled'
    return 'pending'
  }
  const n = Number(val)
  const map = { 1: 'pending', 2: 'in_progress', 3: 'approved', 4: 'rejected', 5: 'cancelled' }
  return map[n] || 'pending'
}

const currentStatusKey = computed(() => normalizeInstanceStatus(instanceInfo.value?.status || instanceInfo.value?.statusText))

const currentStatusTextZh = computed(() => {
  const map = {
    pending: '待审批',
    in_progress: '审批中',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[currentStatusKey.value] || '未知'
})

// Approval Logic
const getCurrentUser = () => {
  try {
    const stored = localStorage.getItem('userInfo')
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error(e)
  }
  return { name: '' }
}

const canApprove = computed(() => {
  const status = currentStatusKey.value
  if (status !== 'pending' && status !== 'in_progress') return false
  
  // If backend provides canApprove flag, use it
  if (instanceInfo.value?.canApprove !== undefined) return instanceInfo.value.canApprove
  
  const currentNodeName = instanceInfo.value?.currentNodeName || ''
  const userName = getCurrentUser().name
  
  if (!currentNodeName || !userName) return false
  
  const checkName = userName + "审批"
  return currentNodeName === checkName || currentNodeName === userName || currentNodeName.includes(userName)
})

const getNodeStatusText = (action) => {
  const map = { 0: '待审批', 1: '待审批', 2: '已通过', 3: '已驳回', 4: '已取消' }
  return map[Number(action)] || '待审批'
}

const nodeNumberClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-500'
  if (text.includes('驳回')) return 'bg-red-500'
  if (text.includes('取消')) return 'bg-gray-400'
  return 'bg-blue-500'
}

const actionBadgeClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-100 text-green-700'
  if (text.includes('驳回')) return 'bg-red-100 text-red-700'
  if (text.includes('取消')) return 'bg-gray-100 text-gray-700'
  return 'bg-blue-100 text-blue-700'
}

const ccUsersNormalized = computed(() => {
  const nodes = workflowNodes.value || []
  const result = []
  nodes.forEach(n => {
    if (Array.isArray(n.ccUsers) && n.ccUsers.length) {
      n.ccUsers.forEach(u => result.push(u))
    }
  })
  return result
})

const workflowNodesPreview = computed(() => {
  const nodes = workflowNodes.value || []
  const recs = approvalRecords.value || []
  
  return nodes.map(n => {
    const nodeRecs = recs.filter(x => x.nodeId === n.id)
    const lastRec = nodeRecs.sort((a,b) => new Date(b.processedAt) - new Date(a.processedAt))[0] || {}
    
    return {
      nodeName: n.nodeName,
      approvers: [{ approverName: n.approverName || n.approvers?.map(a=>a.name).join(',') }],
      action: lastRec.action,
      actionText: lastRec.actionText,
      processedAt: lastRec.processedAt,
      remarks: lastRec.remarks
    }
  })
})

const mapJsonToInitialData = (jsonData, businessType) => {
  try {
    let data = jsonData
    if (typeof jsonData === 'string') {
      data = JSON.parse(jsonData)
    }
    if (!data || typeof data !== 'object') return {}

    if (businessType === '付款申请' || businessType === '付款申请(小额)') {
      const rawVoucherList = Array.isArray(data.voucherList) ? data.voucherList : []
      const normalizedVouchers = rawVoucherList.map(v => ({
        ...v,
        id: v.id ?? v.voucherId
      }))
      
      return {
        ...data,
        supplierName: data.supplier?.name || data.supplierName || '',
        paymentReason: data.payment?.reason || data.paymentReason || '',
        amount: data.payment?.amount || data.amount || data.totalAmount || null,
        totalAmount: data.payment?.totalAmount || data.totalAmount || data.paymentAmount || null,
        expectedPaymentDate: data.payment?.expectedPaymentDate || data.expectedPaymentDate || '',
        paymentMethod: data.payment?.method || data.paymentMethod || '',
        paymentAccountName: data.payment?.accountName || data.paymentAccountName || '',
        payeeName: data.payee?.name || data.payeeName || '',
        payeeAccount: data.payee?.account || data.payeeAccount || '',
        payeeBank: data.payee?.bank || data.payeeBank || '',
        voucherList: normalizedVouchers,
      }
    }
    return data
  } catch (e) {
    console.error('映射初始数据失败:', e)
    return {}
  }
}

const loadDetail = async () => {
  if (!applicationId.value) return
  loading.value = true
  try {
    const res = await approvalApi.getApplicationDetail(applicationId.value)
    if (res.success) {
      const app = res.data
      instanceInfo.value = app.instance || app
      approvalRecords.value = app.approvalRecords || []
      workflowNodes.value = app.workflowNodes || []
      
      const businessType = (app.instance?.businessType) || app.businessType || '付款申请'
      const jsonData = app.instance?.jsonData || app.jsonData || app.applicationData || '{}'
      initialData.value = mapJsonToInitialData(jsonData, businessType)
      
      // Auto expand if only few items? No, default collapse to save space
    } else {
      error.value = res.message || '加载失败'
    }
  } catch (e) {
    error.value = '网络错误'
    console.error(e)
  } finally {
    loading.value = false
  }
}

const handleApprove = async () => {
      approving.value = true
      try {
        const res = await approvalApi.approveInstance(applicationId.value, { remarks: approvalComment.value })
        if (res.success) {
          toast.success('审批通过')
          checkAndPromptNext()
        } else {
          toast.error(res.message || '操作失败')
        }
      } catch (e) {
        toast.error('网络错误')
      } finally {
        approving.value = false
      }
    }

    const handleReject = async () => {
      if (!approvalComment.value) {
        toast.error('请输入驳回意见')
        return
      }
      approving.value = true
      try {
        const res = await approvalApi.rejectInstance(applicationId.value, { remarks: approvalComment.value })
        if (res.success) {
          toast.success('审批驳回')
          checkAndPromptNext()
        } else {
          toast.error(res.message || '操作失败')
        }
      } catch (e) {
        toast.error('网络错误')
      } finally {
        approving.value = false
      }
    }

    const loadPendingApprovals = async () => {
      try {
        const res = await approvalApi.getPendingApprovals({ page: 1, pageSize: 20, status: 'pending' })
        if (res.success || res.succeeded) {
           const data = res.data || res
           let list = []
           if (Array.isArray(data)) {
             list = data
           } else {
             list = data.list || data.items || []
           }
           // Filter out current item
           pendingApprovals.value = list.filter(item => {
             const id = item.id || item.instanceId || item.businessKey
             return id && String(id) !== String(applicationId.value)
           })
        }
      } catch (e) { console.error(e) }
    }

    const checkAndPromptNext = async () => {
      if (props.isModal) {
        emit('refresh')
        emit('close')
        return
      }
      await loadPendingApprovals()
      
      if (pendingApprovals.value.length > 0) {
        setTimeout(() => {
          showNextApprovalDialog.value = true
        }, 300)
      } else {
        toast.success('所有待办已审批完成')
        goBack()
      }
    }

    const handleNextApprovalConfirm = () => {
      showNextApprovalDialog.value = false
      const item = pendingApprovals.value[0]
      if (item) {
         const typeMap = {
            '付款申请': 'payment',
            '付款申请(小额)': 'payment',
            'payment': 'payment',
            '开票申请': 'invoice',
            'Invoice': 'invoice',
            'invoice': 'invoice',
            '公章申请': 'seal',
            '公章申请(合同)': 'seal',
            'seal': 'seal'
          };
          // Try to match businessType or type
          const typeKey = item.businessType || item.type || '';
          const type = typeMap[typeKey] || typeMap[Object.keys(typeMap).find(k => k.toLowerCase() === typeKey.toLowerCase())];
          const id = item.id || item.instanceId || item.businessKey;
          
          if (type && id) {
            router.replace(`/mobile/approval/${type}/${id}`)
          } else {
            goBack()
          }
      } else {
        goBack()
      }
    }

    const goBack = () => {
      if (props.isModal) {
        emit('close')
      } else {
        router.back()
      }
    }

    onMounted(() => {
  loadDetail()
})

// Watch for ID changes if modal is reused
watch(() => props.id, (newId) => {
  if (newId) loadDetail()
})
</script>
